import { useMemo, useState } from 'react';
import { Link, useLocation, type LinkProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Download, Maximize2, Pencil } from 'lucide-react';
import { useProcess } from '@/api/queries/useProcesses';
import { useProcessLayoutForView } from '@/lib/useProcessLayoutForView';
import { useAuthStore } from '@/stores/auth-store';
import { BackButton } from '@/components/back-button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Skeleton,
  Badge,
  Page,
  PageContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  UserCard,
  ProcessFlowVisualization,
  formatDate,
  renderFallback
} from '@bliks-io/ui';
import { ProcessStats } from './components/ProcessStats';
import { MaybeLink } from './components/MaybeLink';
import { ProcessExportDialog } from './components/ProcessExportDialog';
import { ProcessInvolvedEntities } from './components/ProcessInvolvedEntities';
import { ProcessStepsTable } from './components/ProcessStepsTable';
import { ProcessConnectionsMapCard } from './components/ProcessConnectionsMapCard';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePermissions, useIsOrgManager } from '@/hooks/usePermissions';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useProcessEntitySuggestions } from '@/lib/useProcessEntitySuggestions';
import {
  PROCESS_CATEGORY_COLOR_SCHEME,
  PROCESS_DETAIL_BACK_ROUTES,
  PROCESS_STATUS_COLOR_SCHEME,
  processErrorMessageKey,
  processStatusLabel,
  type ProcessDetailOrigin
} from '@/lib/process-utils';
import {
  getEffectiveAccountable,
  getEffectiveAccountableDepartmentAbbreviation
} from '@/components/processes/effective-accountable';
import { aggregateProcessEntities } from './utils/processEntityAggregation';
import type {
  PopulatedUser,
  PopulatedOrgFunction,
  PopulatedSystem,
  Artifact,
  ProcessWithSteps
} from '@bliks-io/contracts';

interface ProcessDetailPageProps {
  processId: string;
}

// Entity usage tracking types
export type EntityUsageRole = 'assigned' | 'recorded' | 'whoElseDoesThis' | 'helper';

export interface EntityUsage {
  stepName: string;
  role: EntityUsageRole;
}

export interface TrackedUser extends PopulatedUser {
  usages: EntityUsage[];
}

export interface TrackedTeam extends PopulatedOrgFunction {
  usages: EntityUsage[];
}

export interface TrackedSystem extends PopulatedSystem {
  usages: EntityUsage[];
}

export interface TrackedArtifact extends Artifact {
  usages: EntityUsage[];
}

function ProcessDetailLoading() {
  return (
    <Page>
      <PageContent>
        <Skeleton className="h-8 w-64 mb-8" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}

function ProcessDetailError({
  backTo,
  slug,
  error
}: {
  backTo: LinkProps['to'];
  slug: string;
  error: unknown;
}) {
  const { t } = useTranslation();
  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to={backTo} params={{ slug }} />
        </div>
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">{t(processErrorMessageKey(error))}</p>
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}

function ProcessDetailsCard({
  process,
  slug,
  usersById
}: {
  process: ProcessWithSteps;
  slug: string;
  /**
   * User lookup for deleted detection on owner / department manager — both
   * arrive backend-populated with `deletedAt` stripped, so the tombstone only
   * survives in the lookup.
   */
  usersById?: ReadonlyMap<string, { deletedAt?: number }>;
}) {
  const { t } = useTranslation();
  const dept = typeof process.department === 'object' ? process.department : null;
  const accountable = getEffectiveAccountable(process.accountable, dept?.manager);
  const accountableDepartmentAbbreviation = getEffectiveAccountableDepartmentAbbreviation(
    process.accountable,
    dept
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('processes.details')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium text-muted-foreground">{t('common.Name')}</label>
          <p className="mt-1 text-base">{process.name}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground">
            {t('common.Description')}
          </label>
          <p className="mt-1 text-base">{renderFallback(process.description)}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground">
            {t('processes.department')}
          </label>
          <p className="mt-1 text-base">
            {dept ? (
              <Link
                to="/$slug/departments/$departmentId"
                params={{ slug, departmentId: dept._id }}
                className="hover:underline"
              >
                {dept.name}
              </Link>
            ) : (
              renderFallback(undefined)
            )}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t('processes.Accountable')}
            </label>
            <div className="mt-1">
              {accountable ? (
                <MaybeLink
                  to="/$slug/org-members/$orgMemberId"
                  params={{ slug, orgMemberId: accountable._id }}
                  className="block"
                  disabled={Boolean(usersById?.get(accountable._id)?.deletedAt)}
                >
                  <UserCard
                    user={{
                      ...accountable,
                      departmentAbbreviation: accountableDepartmentAbbreviation,
                      deletedAt: usersById?.get(accountable._id)?.deletedAt
                    }}
                    variant="with-role"
                    itemVariant="outline"
                  />
                </MaybeLink>
              ) : (
                <p className="text-base">{renderFallback(undefined)}</p>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t('processes.ProcessOwner')}
            </label>
            <div className="mt-1">
              {process.owner ? (
                <MaybeLink
                  to="/$slug/org-members/$orgMemberId"
                  params={{ slug, orgMemberId: process.owner._id }}
                  className="block"
                  disabled={Boolean(usersById?.get(process.owner._id)?.deletedAt)}
                >
                  <UserCard
                    user={{
                      ...process.owner,
                      departmentAbbreviation: process.owner.department?.abbreviation,
                      deletedAt: usersById?.get(process.owner._id)?.deletedAt
                    }}
                    variant="with-role"
                    itemVariant="outline"
                  />
                </MaybeLink>
              ) : (
                <p className="text-base">{renderFallback(undefined)}</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t('common.Created')}
            </label>
            <p className="mt-1 text-base">{formatDate(process.createdAt)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t('common.Updated')}
            </label>
            <p className="mt-1 text-base">{formatDate(process.updatedAt)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProcessDetailPage({ processId }: ProcessDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const location = useLocation();
  const { data, isLoading, error } = useProcess(processId);
  const layout = useProcessLayoutForView(processId, true);
  const { can } = usePermissions();
  const assigneeLookup = useAssigneeLookup();
  const [exportOpen, setExportOpen] = useState(false);
  const isOrgManager = useIsOrgManager();

  const fromState = (location.state as { from?: ProcessDetailOrigin } | null)?.from;
  const backTo = PROCESS_DETAIL_BACK_ROUTES[fromState ?? 'all-processes'];

  const process = data?.process;
  const capabilities = data?.capabilities;
  const status = data?.status ?? 'in_recording';
  const pendingSurveyCount =
    status === 'recording_complete' ? (data?.pendingSurveyCount ?? null) : null;
  // Badge renders a div — tabIndex makes the tooltip keyboard-reachable.
  const statusBadge = (
    <Badge
      colorScheme={PROCESS_STATUS_COLOR_SCHEME[status]}
      tabIndex={pendingSurveyCount === null ? undefined : 0}
    >
      {processStatusLabel(status, t)}
    </Badge>
  );

  // Set page title with process name
  useDocumentTitle('pageTitles.processDetail', { name: process?.name || 'Loading...' });

  const canUpdate = capabilities?.canUpdate ?? false;
  const canReadProcessMap = can('ProcessMap', 'read');
  const canEditConnections = canUpdate && can('ProcessConnection', 'create');

  // Calculate process stats and extract involved entities
  const {
    totalSteps,
    involvedPeople,
    involvedTeams,
    involvedFunctions,
    involvedSystems,
    involvedArtifacts,
    involvedExternalContacts
  } = useMemo(
    () => aggregateProcessEntities(process, t, assigneeLookup),
    [process, t, assigneeLookup]
  );

  const entitySuggestions = useProcessEntitySuggestions(process, data?.entitySuggestions);

  if (isLoading) {
    return <ProcessDetailLoading />;
  }

  if (error || !process) {
    return <ProcessDetailError backTo={backTo} slug={slug} error={error} />;
  }

  // Shared by the steps table and the export dialog (Excel export).
  const stepRecordings = process.processStepRecordings || [];
  const stepCapacities = data?.capacity?.perStep;
  const stepCoverage = data?.capacity?.coverage;

  return (
    <Page>
      <PageContent>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <BackButton to={backTo} params={{ slug }} />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold leading-7">{process.name}</h1>
                {pendingSurveyCount === null ? (
                  statusBadge
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>{statusBadge}</TooltipTrigger>
                    <TooltipContent>
                      {t('processes.pendingSurveyCount', { count: pendingSurveyCount })}
                    </TooltipContent>
                  </Tooltip>
                )}
                {process.category && (
                  <Badge colorScheme={PROCESS_CATEGORY_COLOR_SCHEME[process.category]}>
                    {t(`processMap.lanes.${process.category}`)}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm font-normal leading-5 text-muted-foreground">
                {t('processes.detailDescription')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isOrgManager && (
              <Button variant="outline" onClick={() => setExportOpen(true)}>
                <Download className="h-4 w-4" />
                {t('processDetail.export.button')}
              </Button>
            )}
            {canUpdate && process.recording?.startDate && (
              <Button asChild>
                <Link to="/$slug/processes/editor/$processId" params={{ slug, processId }}>
                  <Pencil className="h-4 w-4" />
                  {t('processDetail.editProcess')}
                </Link>
              </Button>
            )}
          </div>
        </div>

        {capabilities?.canViewProcessInsights && (
          <ProcessStats
            totalSteps={totalSteps}
            averageRating={data?.averageRating ?? null}
            capacityBinding={data?.capacity?.overall ?? null}
          />
        )}

        <div className="space-y-6">
          {/* Two-column layout: Process Details + BPMN Preview */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column: Process Details */}
            <ProcessDetailsCard process={process} slug={slug} usersById={assigneeLookup.users} />

            {/* Right Column: BPMN Preview */}
            <Card>
              <CardHeader>
                <div className="flex flex-row items-center justify-between">
                  <CardTitle>{t('processFlow.bpmnPreview')}</CardTitle>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        asChild
                        variant="outline"
                        size="icon"
                        aria-label={t('processFlow.openFullscreen')}
                      >
                        <Link
                          to="/$slug/processes/all-processes/$processId/flow-fullscreen"
                          params={{ slug, processId }}
                        >
                          <Maximize2 className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('processFlow.openFullscreen')}</TooltipContent>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {layout.isPending ? (
                  <Skeleton
                    role="status"
                    aria-label={t('common.loading')}
                    className="h-125 w-full"
                  />
                ) : (
                  <ProcessFlowVisualization
                    process={process}
                    height="500px"
                    showMinimap={false}
                    showControls
                    showSensitiveFields={capabilities?.canViewSensitiveFields}
                    showVariantCheck={capabilities?.canViewProcessInsights}
                    savedPositions={layout.savedPositions}
                    swimlaneOrder={layout.swimlaneOrder}
                    laneColors={layout.laneColors}
                    openAssignments={data?.openAssignments}
                    events={data?.events}
                    notes={data?.notes}
                    assigneeLookup={assigneeLookup}
                    entitySuggestions={entitySuggestions}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {canReadProcessMap && (
            <ProcessConnectionsMapCard processId={processId} canEdit={canEditConnections} />
          )}

          <ProcessInvolvedEntities
            people={involvedPeople}
            teams={involvedTeams}
            functions={involvedFunctions}
            systems={involvedSystems}
            artifacts={involvedArtifacts}
            externalContacts={involvedExternalContacts}
          />

          <ProcessStepsTable
            steps={stepRecordings}
            stepCapacities={stepCapacities}
            stepCoverage={stepCoverage}
            isLoading={isLoading}
            showManagerColumns={capabilities?.canViewSensitiveFields ?? false}
            showVariantCheck={capabilities?.canViewProcessInsights ?? false}
          />
        </div>

        {/* Always mounted; only org-managers can open it (the trigger is gated). */}
        <ProcessExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          processId={processId}
          processName={process.name}
          steps={stepRecordings}
          stepCapacities={stepCapacities}
        />
      </PageContent>
    </Page>
  );
}
